import React, { useState, useEffect, useContext, useMemo } from "react";
import { useTable } from "@refinedev/antd";
import {
  Input,
  Table,
  theme,
  type TableColumnsType,
  Form,
  Grid,
  Flex,
  Typography,
  Tag,
  Button,
  Badge,
  Result,
} from "antd";
import {
  CrudFilters,
  HttpError,
  useGetIdentity,
  useNavigation,
} from "@refinedev/core";
import { PlusOutlined } from "@ant-design/icons";
import { PaginationTotal } from "@/components/pagination-total";
import { TFunction, i18n as I18nType } from "i18next";
import { useTranslation } from "react-i18next";
import { SmartMenuContext } from "@/config/context/smartMenuContext";
import { StoresUserPermissionsCreate } from "./userPermissionsCreate";
import { User } from "@/types/schema/schema.types";
import { useNavigate } from "react-router-dom";
import { API_URL_PERMISSIONS_STORES } from "@/services/XCONS_ENV";
import { KeyRound } from "@/components/icon/Key";
import { NormalModal } from "@/components";
import { FILAS_POR_PAGINA, OPCIONES_POR_PAGINA } from "@/config/paginacion";
import { nivelDeOperadores } from "@/utilities/nivel-de-operadores";
import { Robot2Line } from "@/components/icon/RobotIcon";
import { AgentSummary, agentApi } from "@/pages/stores/agent/api";
import { asistentesPorUsuario } from "@/pages/stores/agent/asistentes";
import { AsistenteDrawer } from "@/pages/stores/agent/AsistenteDrawer";

const { Title, Text } = Typography;
const { Search } = Input;

export const StoresUserPermissionsList: React.FC<{
  children?: React.ReactNode;
}> = ({ children }) => {
  const screens = Grid.useBreakpoint();
  const { token } = theme.useToken();
  const { data: dataLoguinUser } = useGetIdentity<User>();
  const { smartMenuName } = useContext(SmartMenuContext);
  const [typeIdentifier, setTypeIdentifier] = useState("store");
  const { push } = useNavigation();
  const storeIdentifier = encodeURIComponent(
    smartMenuName?.replace(/"/g, "").toLowerCase(),
  );
  // `store_operators` del logueado en ESTE store (xcons-docs#537): Admin agrega operadores,
  // Viewer sólo mira, y No llega acá únicamente por URL, porque el menú no ofrece la sección.
  // Hasta que la identidad llega no se decide nada: con `undefined` el helper diría No y un
  // Admin que recarga la página vería "Sin acceso" un instante.
  const nivelOperadores = dataLoguinUser
    ? nivelDeOperadores(
        dataLoguinUser,
        smartMenuName?.replace(/"/g, "").toLowerCase() ?? "",
      )
    : undefined;

  const { t, i18n }: { t: TFunction; i18n: I18nType } = useTranslation();

  // Los asistentes del store (D62): la fila de un operador con `agent: admin` abre su drawer. La
  // lista de operadores no trae los permisos de cada uno; la de asistentes es exactamente eso.
  const storeCode = smartMenuName?.replace(/"/g, "").toLowerCase() ?? "";
  const [asistentes, setAsistentes] = useState<AgentSummary[]>([]);
  const [asistenteAbierto, setAsistenteAbierto] = useState<number | null>(null);
  const porUsuario = useMemo(
    () => asistentesPorUsuario(asistentes),
    [asistentes],
  );
  const recargarAsistentes = () =>
    storeCode
      ? agentApi
          .asistentes(storeCode)
          .then(setAsistentes)
          .catch(() => setAsistentes([]))
      : Promise.resolve();

  useEffect(() => {
    setAsistenteAbierto(null);
    void recargarAsistentes();
  }, [storeCode]);

  const onSearch = (params: any) => {
    const filters: CrudFilters = [];
    const { name_search } = params;

    filters.push({
      field: "search",
      operator: "contains",
      value: name_search,
    });

    return filters;
  };

  const {
    tableQuery: { data, isLoading, isFetching, refetch },
    searchFormProps,
    tableProps,
  } = useTable<HttpError>({
    resource: `permissions/${typeIdentifier}/${storeIdentifier}/users`,
    pagination: {
      pageSize: FILAS_POR_PAGINA,
    },
    sorters: {
      initial: [
        {
          field: "user.firstname",
          order: "asc",
        },
      ],
    },
    onSearch: onSearch,
    filters: {
      permanent: [{ field: "search", operator: "eq", value: "" }],
    },
  });

  const isLoadingTable = !data || isFetching;

  const total = data?.total || 0;

  const dataSource = useMemo(
    () => (isLoadingTable ? [] : data?.data || []),
    [isLoadingTable, data],
  );

  const columns: TableColumnsType<any> = [
    {
      key: "firstname",
      title: t("products.show.tableName"),
      sorter: (a, b) => a.user.firstname.localeCompare(b.user.firstname),
      dataIndex: "user.firstname",
      render: (_, record) => {
        const esAsistente = porUsuario.has(Number(record?.user?.userId));
        return (
          <Flex justify="space-between" align="center" gap={8}>
            <Text
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              {record?.user?.firstname} {record?.user?.lastname}
              {dataLoguinUser?.email === record?.user?.email && (
                <Tag style={{ color: token.colorPrimary, marginLeft: "8px" }}>
                  {t("products.show.you")}
                </Tag>
              )}
            </Text>
            {esAsistente && (
              <Button
                size="small"
                icon={<Robot2Line />}
                style={{ flexShrink: 0 }}
                onClick={(e) => {
                  // La fila navega a la ficha del operador: el botón abre el drawer en su lugar.
                  e.stopPropagation();
                  setAsistenteAbierto(Number(record.user.userId));
                }}
              >
                Asistente IA
              </Button>
            )}
          </Flex>
        );
      },
      width: "30%",
    },
    {
      key: "email",
      title: t("pages.login.fields.email"),
      dataIndex: "user.email",
      render: (_, record) => {
        return <Text>{record?.user?.email}</Text>;
      },
      width: "35%",
    },
    {
      key: "whatsapp",
      title: t("linkedProducts.modal.whatsapp") || "WhatsApp",
      dataIndex: "user.whatsapp",

      render: (_, record) => {
        return (
          <Text>{record?.user?.whatsapp ? record?.user?.whatsapp : "-"}</Text>
        );
      },
      width: "25%",
    },
    {
      key: "status",
      title: t("orders.table.status") || "Status",
      dataIndex: "isActive",
      sorter: (a, b) => a.isActive - b.isActive,
      align: "center",
      width: "10%",
      render: (_, record) => {
        return (
          <Badge
            status={record.isActive ? "success" : "default"}
            text={
              record.isActive
                ? t("filter.products.labelStatusActive")
                : t("filter.products.labelStatusDesactive")
            }
          />
        );
      },
    },
  ];

  const HeaderRow = React.memo(() => (
    <>
      <Flex flex="block" gap="large" wrap={screens.sm ? false : true}>
        <Form {...searchFormProps} style={{ width: "100%" }}>
          <Form.Item name="name_search">
            <Search
              placeholder={t("search.placeholder.byUserEmailWhatsapp")}
              allowClear
              onSearch={(value) => {
                if (value.trim() === "") {
                  searchFormProps.form?.setFieldsValue({
                    name_search: undefined,
                  });
                }
                searchFormProps.form?.submit();
              }}
            />
          </Form.Item>
        </Form>

        {nivelOperadores === "admin" && (
          <Flex
            gap="large"
            justify="center"
            style={{ width: screens.sm ? "auto" : "100%" }}
            className="add-user-button"
          >
            <NormalModal
              title={t("button.add.operator")}
              footer={null}
              trigger={
                <Button
                  icon={<PlusOutlined />}
                  iconPosition="end"
                  type="primary"
                >
                  {t("button.add.operator")}
                </Button>
              }
            >
              <StoresUserPermissionsCreate
                t={t}
                refetch={refetch}
                sectionType="permission"
              />
            </NormalModal>
          </Flex>
        )}
      </Flex>
    </>
  ));

  const navigate = useNavigate();

  const handleRowClick = (record: any) => {
    navigate(`/stores/operators/${storeIdentifier}/${record?.user?.userId}`, {
      state: { record: record },
    });
  };

  const generateRowKey = (record: any) => {
    if (record && typeof record?.user?.userId !== "undefined") {
      return `${record.user.userId}`;
    }
    console.warn(
      "generateRowKey: userId no encontrado en el registro. Registro:",
      record,
    );
    return `missing-userId-${Math.random().toString(36).substring(7)}`;
  };

  if (nivelOperadores === "no_access") {
    // La API ya contesta 403 al listado; esto evita una tabla vacía con un toast de error, que
    // es lo que se vería si alguien llega con la URL de otro store.
    return (
      <Result
        status="403"
        title={t("storeoperators.noaccess.title", "Sin acceso")}
        subTitle={t(
          "storeoperators.noaccess.body",
          "No tenés acceso a Operadores del Store en esta tienda.",
        )}
      />
    );
  }

  return (
    <>
      <Title level={3} style={{ display: "flex", alignItems: "center" }}>
        <KeyRound style={{ marginRight: "10px" }} />
        {`${t("contacts.tag.storeusers")}`}
      </Title>
      <Table
        {...tableProps}
        style={{ cursor: "pointer" }}
        scroll={{ x: true }}
        title={() => <HeaderRow />}
        rowKey={(record) => generateRowKey(record)}
        onRow={(record) => ({
          onClick: () => {
            handleRowClick(record);
          },
        })}
        dataSource={dataSource}
        columns={columns}
        loading={isFetching}
        locale={{
          triggerAsc: t("table.triggerAsc"),
          triggerDesc: t("table.triggerDesc"),
          cancelSort: t("table.cancelSort"),
        }}
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          pageSizeOptions: OPCIONES_POR_PAGINA,
          showQuickJumper: false,
          locale: {
            items_per_page: t("table.itemsPerPage"),
            prev_page: t("table.prevPage"),
            next_page: t("table.nextPage"),
            jump_to: t("table.jumpTo"),
            jump_to_confirm: t("table.jumpToConfirm"),
            page: t("table.page"),
            next_5: t("table.next5"),
            prev_5: t("table.prev5"),
          },
          showTotal: (total) => (
            <PaginationTotal total={total} entityName="operators" />
          ),
        }}
      />
      <AsistenteDrawer
        storeCode={storeCode}
        asistente={porUsuario.get(asistenteAbierto ?? -1) ?? null}
        asistentes={asistentes}
        editable={nivelOperadores === "admin"}
        onClose={() => setAsistenteAbierto(null)}
        onCambio={() => void recargarAsistentes()}
      />
    </>
  );
};
